<script setup>
definePage({
  name: 'shipment-warehouse-cotation',
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
  },
})

// Container-wide cotation — the in-app replacement for the warehouse's
// Excel "TEST COTATION" sheet. Every parcel of the container grouped by
// client: PL vs measured dims, TYPE, price, client total, ETAT and FFI
// invoice n°. Measurements are typed straight into the table (saved via
// PUT warehouse/inspections/{id}); a parcel is priced only once measured.
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { SHELF_LOCATIONS } from '@/composables/useShelfConfig'
import InsertUnfoundParcelDialog from '../client-groups/components/InsertUnfoundParcelDialog.vue'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()

const containerId = computed(() => route.params.containerId)

const WORKFLOW_STATES = [
  { key: 'EN_COURS', label: 'EN COURS', color: '#6B7280' },
  { key: 'MP_ENVOYER', label: 'MP ENVOYER', color: '#CA8A04' },
  { key: 'PAYER', label: 'PAYER', color: '#B91C1C' },
  { key: 'PAYER_ET_LIVRER', label: 'PAYER ET LIVRER', color: '#16A34A' },
]

// Fixed palette for the "found in warehouse" marker — keep in sync with
// IncomingParcel::HIGHLIGHT_COLORS on the backend.
const HIGHLIGHT_COLORS = [
  { key: 'red', hex: '#EF4444' },
  { key: 'orange', hex: '#F97316' },
  { key: 'yellow', hex: '#EAB308' },
  { key: 'green', hex: '#22C55E' },
  { key: 'blue', hex: '#3B82F6' },
  { key: 'purple', hex: '#A855F7' },
]

const highlightHex = key => HIGHLIGHT_COLORS.find(c => c.key === key)?.hex || null

const workflowState = key => WORKFLOW_STATES.find(s => s.key === key) || WORKFLOW_STATES[0]

const cotation = ref(null)
const isLoading = ref(false)
const isExporting = ref(false)
const team = ref('GD')

// Filters (client-side)
const search = ref('')
const filterEtat = ref(null)
const onlyUnmeasured = ref(false)

// Per-parcel drafts of the editable cells, keyed by parcel_id.
const drafts = reactive({})
const savingRows = reactive({})

const fmtMga = n => (n === null || n === undefined ? '' : Math.round(n).toLocaleString('fr-FR'))
const fmtVol = v => (v === null || v === undefined ? '' : Number(v).toFixed(3))
const fmtDims = d => (d && d.length && d.width && d.height ? `${+d.length}×${+d.width}×${+d.height}` : '')

// Saved value of an editable cell (used until the user types something).
const savedValue = (parcel, field) => ({
  length: parcel.measured?.length,
  width: parcel.measured?.width,
  height: parcel.measured?.height,
  type: parcel.type,
  shelf_gondole: parcel.shelf_gondole,
  shelf_ligne: parcel.shelf_ligne,
  shelf_location: parcel.shelf_location,
}[field] ?? '')

const cell = (parcel, field) => drafts[parcel.parcel_id]?.[field] ?? savedValue(parcel, field)

const setCell = (parcel, field, value) => {
  if (!drafts[parcel.parcel_id]) drafts[parcel.parcel_id] = {}
  drafts[parcel.parcel_id][field] = value
}

const load = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true
  try {
    const { data } = await axios.get(`shipment/warehouse/containers/${containerId.value}/cotation`, {
      params: { team: team.value },
    })

    cotation.value = data.data

    // Drop drafts so cells show the saved values.
    Object.keys(drafts).forEach(k => delete drafts[k])
  } catch (e) {
    console.error('cotation load failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    isLoading.value = false
  }
}

const categories = computed(() => (cotation.value?.rates || []).map(r => r.category))

// "13-19" looks like a bag code: match it exactly (a substring search also
// returned 13-190, 13-191…). Anything else is a free-text "contains" search.
const BAG_CODE_RE = /^\d+-\d+$/

const parcelMatches = (p, q) => {
  if (BAG_CODE_RE.test(q)) return (p.bag_code || '').trim() === q

  return [p.barcode, p.bag_code, p.product, p.agent, p.shelf_location].some(v => (v || '').toLowerCase().includes(q))
}

// Rows that are the result of entering Quantity > 1 in the measure editor
// (N identical boxes sharing one bag_code + dims). The table stays fully
// editable per row (unlike mobile's single-merged-row view), so instead of
// collapsing duplicates we just badge the ×N count on the first row of
// each identical cluster — the other rows stay visible and editable.
const rowKey = p => (p.measured
  ? `${p.bag_code}|${p.measured.length}|${p.measured.width}|${p.measured.height}|${p.measured.weight}`
  : `single-${p.parcel_id}`)

const rowQuantityBadge = (p, parcels) => {
  const key = rowKey(p)
  const group = parcels.filter(x => rowKey(x) === key)
  if (group.length < 2) return null

  return group[0].parcel_id === p.parcel_id ? group.length : null
}

const filteredClients = computed(() => {
  const q = (search.value || '').trim().toLowerCase()

  return (cotation.value?.clients || [])
    .filter(c => !filterEtat.value || (c.key !== '__unassigned__' && c.workflow_status === filterEtat.value))
    .map(c => {
      let parcels = c.parcels
      if (onlyUnmeasured.value) parcels = parcels.filter(p => p.price_mga === null)
      if (q) {
        const clientHit = !BAG_CODE_RE.test(q)
          && ((c.customer?.name || '').toLowerCase().includes(q) || (c.customer?.phone || '').includes(q))

        if (!clientHit) parcels = parcels.filter(p => parcelMatches(p, q))
      }

      return { ...c, visibleParcels: parcels }
    })
    .filter(c => c.visibleParcels.length > 0)
})

// ---- saving a parcel row ---------------------------------------------------
const toNumber = v => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')))

// A parcel can be measured once it is in a client group; a parcel that has a
// client but was never grouped is attached to that client's group first.
const canEdit = (parcel, client) => !!(parcel.inspection_id || parcel.customer?.id || client?.group_id)

const ensureInspection = async (parcel, client) => {
  if (parcel.inspection_id) return parcel.inspection_id

  const { data } = await axios.post(`shipment/warehouse/parcels/${parcel.parcel_id}/attach-to-group`, {
    container_id: containerId.value,
    group_id: client?.group_id || null,
  })

  parcel.inspection_id = data.data.inspection_id
  parcel.group_id = data.data.group_id

  return parcel.inspection_id
}

const saveRow = async (parcel, client) => {
  if (!canEdit(parcel, client)) return

  const d = Object.fromEntries(
    ['length', 'width', 'height', 'type', 'shelf_gondole', 'shelf_ligne', 'shelf_location'].map(f => [f, cell(parcel, f)]),
  )

  const dims = [d.length, d.width, d.height].map(toNumber)
  const hasAllDims = dims.every(v => v !== null && !Number.isNaN(v) && v > 0)
  const hasSomeDims = dims.some(v => v !== null)

  // Wait until the three dimensions are typed before saving a measurement.
  if (hasSomeDims && !hasAllDims) return

  const body = { pricing_category: d.type }
  if (hasAllDims) {
    body.arrival_length = dims[0]
    body.arrival_width = dims[1]
    body.arrival_height = dims[2]
  }
  // A parcel is either in the numbered grid or at a named location (e.g.
  // TAMATAVE), never both — whichever side has a value wins and clears
  // the other. shelf_location is always sent explicitly (string or null)
  // so the Tamatave toggle button can reliably clear it.
  const shelfLocation = (d.shelf_location || '').trim() || null
  body.shelf_location = shelfLocation
  if (shelfLocation) {
    body.shelf_gondole = null
    body.shelf_ligne = null
  } else {
    if (toNumber(d.shelf_gondole)) body.shelf_gondole = toNumber(d.shelf_gondole)
    if (toNumber(d.shelf_ligne)) body.shelf_ligne = toNumber(d.shelf_ligne)
  }

  const unchanged = body.pricing_category === parcel.type
    && (!hasAllDims || (dims[0] === parcel.measured?.length && dims[1] === parcel.measured?.width && dims[2] === parcel.measured?.height))
    && (body.shelf_gondole ?? null) === (parcel.shelf_gondole ?? null)
    && (body.shelf_ligne ?? null) === (parcel.shelf_ligne ?? null)
    && (body.shelf_location ?? null) === (parcel.shelf_location ?? null)

  if (unchanged) return

  savingRows[parcel.parcel_id] = true
  try {
    const inspectionId = await ensureInspection(parcel, client)

    await axios.put(`shipment/warehouse/inspections/${inspectionId}`, body)
    await load({ silent: true })
  } catch (e) {
    console.error('save parcel failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    delete savingRows[parcel.parcel_id]
  }
}

// A parcel is either in the numbered Gondole×Ligne grid or at a named
// location (e.g. TAMATAVE) — toggling clears the G/L draft too, then
// saves immediately through the same path as a dimension/type edit.
const toggleShelfLocation = (parcel, client, key) => {
  if (!canEdit(parcel, client)) return
  if (cell(parcel, 'shelf_location') === key) {
    setCell(parcel, 'shelf_location', '')
  } else {
    setCell(parcel, 'shelf_location', key)
    setCell(parcel, 'shelf_gondole', '')
    setCell(parcel, 'shelf_ligne', '')
  }
  saveRow(parcel, client)
}

// ---- "found in warehouse" color marker -----------------------------------
// Purely visual, independent of grouping/measurement — works even on an
// un-grouped parcel, since it's just a flag for the physical search.
const setHighlight = async (parcel, color) => {
  const previous = parcel.highlight_color
  if (color === previous) return

  parcel.highlight_color = color
  try {
    await axios.patch(`shipment/warehouse/parcels/${parcel.parcel_id}/highlight`, { color })
  } catch (e) {
    parcel.highlight_color = previous
    console.error('set highlight failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  }
}

// ---- ETAT + FFI per client ---------------------------------------------------
const paidDialog = reactive({ open: false, client: null, ffi: '', saving: false })

const changeEtat = async (client, newKey) => {
  if (!client.group_ids.length || newKey === client.workflow_status) return
  if (newKey === 'PAYER') {
    Object.assign(paidDialog, { open: true, client, ffi: client.external_invoice_ref || '' })

    return
  }
  await submitEtat(client, newKey, null)
}

const submitEtat = async (client, newKey, ffi) => {
  try {
    for (const groupId of client.group_ids) {
      const body = { workflow_status: newKey }
      if (ffi) body.external_invoice_number = ffi
      await axios.put(`shipment/warehouse/groups/${groupId}/workflow-status`, body)
    }
    showSnackbar({ color: 'success', text: t('shipment.workflow_updated') || 'Statut mis à jour' })
  } catch (e) {
    console.error('workflow update failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    await load({ silent: true })
  }
}

const confirmPaid = async () => {
  paidDialog.saving = true
  await submitEtat(paidDialog.client, 'PAYER', paidDialog.ffi.trim() || null)
  paidDialog.saving = false
  paidDialog.open = false
}

const saveFfi = async (client, value) => {
  const ffi = (value || '').trim()
  if (!client.group_id || ffi === (client.external_invoice_ref || '')) return
  try {
    for (const groupId of client.group_ids) {
      await axios.put(`shipment/warehouse/groups/${groupId}/external-invoice-ref`, {
        external_invoice_number: ffi || null,
      })
    }
    showSnackbar({ color: 'success', text: 'N° facture enregistré' })
  } catch (e) {
    console.error('FFI update failed:', e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    await load({ silent: true })
  }
}

// ---- Excel export (transition / backup) --------------------------------------
const exportExcel = async () => {
  isExporting.value = true
  try {
    const response = await axios.get(`shipment/warehouse/containers/${containerId.value}/test-cotation`, {
      params: { team: team.value },
      responseType: 'blob',
    })

    const url = URL.createObjectURL(new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }))

    const link = document.createElement('a')

    link.href = url
    link.download = `TEST_COTATION_${cotation.value?.container?.reference_code || containerId.value}_${team.value}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('export failed:', e)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    isExporting.value = false
  }
}

// Parcel found at the warehouse but missing from the PL: same form as the
// client-groups page (creates parcel + client group + measurement).
const showAddParcel = ref(false)

const onParcelAdded = async () => {
  showSnackbar({ color: 'success', text: 'Colis ajouté à la cotation' })
  await load({ silent: true })
}

const setTeam = async value => {
  team.value = value
  await load()
}

onMounted(load)
</script>

<template>
  <div class="cotation-page">
    <!-- Header -->
    <VCard class="mb-4">
      <VCardText class="d-flex flex-wrap align-center gap-2">
        <h2 class="text-h4 me-4">
          Cotation
        </h2>
        <VChip
          v-if="cotation"
          color="primary"
          variant="elevated"
          prepend-icon="tabler-container"
        >
          {{ cotation.container.reference_code }}
        </VChip>
        <!-- Team scope: two plain buttons (VBtnToggle squeezes its
             buttons inside this flex header). -->
        <div class="d-flex gap-1 flex-shrink-0">
          <VBtn
            v-for="opt in [{ value: 'GD', label: 'GD (GODANA)' }, { value: 'all', label: 'Toutes les équipes' }]"
            :key="opt.value"
            size="small"
            color="primary"
            :variant="team === opt.value ? 'elevated' : 'outlined'"
            :disabled="isLoading"
            @click="team !== opt.value && setTeam(opt.value)"
          >
            {{ opt.label }}
          </VBtn>
        </div>
        <VSpacer />
        <VBtn
          variant="tonal"
          size="small"
          prepend-icon="tabler-refresh"
          :loading="isLoading"
          @click="load"
        >
          Actualiser
        </VBtn>
        <VBtn
          color="warning"
          variant="tonal"
          size="small"
          prepend-icon="tabler-package-import"
          :disabled="!cotation"
          @click="showAddParcel = true"
        >
          Ajouter un colis
        </VBtn>
        <VBtn
          color="success"
          variant="tonal"
          size="small"
          prepend-icon="tabler-file-spreadsheet"
          :loading="isExporting"
          @click="exportExcel"
        >
          Export Excel
        </VBtn>
        <VBtn
          variant="tonal"
          size="small"
          prepend-icon="tabler-file-spreadsheet"
          :to="`/shipment/warehouse/pl-files/${containerId}`"
        >
          Fichiers PL
        </VBtn>
        <VBtn
          variant="tonal"
          size="small"
          prepend-icon="tabler-users-group"
          :to="`/shipment/warehouse/client-groups/${containerId}`"
        >
          {{ t('shipment.client_groups') }}
        </VBtn>
        <VBtn
          icon
          variant="text"
          @click="router.back()"
        >
          <VIcon icon="tabler-arrow-left" />
        </VBtn>
      </VCardText>

      <!-- KPIs -->
      <VCardText
        v-if="cotation"
        class="pt-0"
      >
        <VRow dense>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-medium-emphasis">
              Clients
            </div>
            <div class="text-h5">
              {{ cotation.totals.clients }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-medium-emphasis">
              Colis mesurés
            </div>
            <div class="text-h5">
              {{ cotation.totals.measured }} / {{ cotation.totals.parcels }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-medium-emphasis">
              Volume mesuré
            </div>
            <div class="text-h5">
              {{ fmtVol(cotation.totals.volume) }} m³
            </div>
          </VCol>
          <VCol
            cols="6"
            md="3"
          >
            <div class="text-caption text-medium-emphasis">
              Montant total
            </div>
            <div class="text-h5 text-primary">
              {{ fmtMga(cotation.totals.total_mga) }} MGA
            </div>
          </VCol>
          <VCol
            cols="12"
            md="3"
            class="d-flex flex-wrap gap-1 align-center"
          >
            <VChip
              v-for="s in WORKFLOW_STATES"
              :key="s.key"
              size="small"
              :color="s.color"
              :variant="filterEtat === s.key ? 'elevated' : 'tonal'"
              @click="filterEtat = filterEtat === s.key ? null : s.key"
            >
              {{ s.label }} · {{ cotation.totals.by_etat[s.key]?.clients || 0 }}
            </VChip>
          </VCol>
        </VRow>
        <div class="text-caption text-medium-emphasis mt-2">
          Tarifs :
          <span
            v-for="r in cotation.rates"
            :key="r.category"
            class="me-3"
          >
            <strong>{{ r.category }}</strong>
            <template v-if="r.currency === 'USD'"> {{ r.rate }} $ × {{ fmtMga(r.usd_to_mga) }} =</template>
            {{ fmtMga(r.rate_mga) }} MGA/{{ r.calculation_type === 'volume' ? 'm³' : 'kg' }}
          </span>
          · minimum 30 000 MGA, arrondi au millier supérieur · prix seulement après mesure
        </div>
      </VCardText>

      <!-- Filters -->
      <VCardText class="pt-0 d-flex flex-wrap gap-3 align-center">
        <VTextField
          v-model="search"
          density="compact"
          hide-details
          clearable
          prepend-inner-icon="tabler-search"
          placeholder="Client, téléphone, tracking, sac, produit…"
          style="max-inline-size: 380px;"
        />
        <VSwitch
          v-model="onlyUnmeasured"
          density="compact"
          hide-details
          color="warning"
          label="Non mesurés seulement"
        />
      </VCardText>
    </VCard>

    <VProgressLinear
      v-if="isLoading"
      indeterminate
      color="primary"
    />

    <!-- The sheet -->
    <VCard v-if="cotation">
      <div class="cotation-scroll">
        <table class="cotation-table">
          <thead>
            <tr>
              <th
                title="Marquer un colis trouvé dans l'entrepôt"
                class="hl-col"
              />
              <th>Rayon</th>
              <th>Agent</th>
              <th>Sac</th>
              <th>Tracking</th>
              <th>Produit</th>
              <th>PL L×l×H</th>
              <th>PL m³</th>
              <th class="measure">
                L
              </th>
              <th class="measure">
                l
              </th>
              <th class="measure">
                H
              </th>
              <th>m³</th>
              <th>TYPE</th>
              <th class="num">
                Prix (MGA)
              </th>
              <th>Remarque</th>
            </tr>
          </thead>
          <tbody
            v-for="client in filteredClients"
            :key="client.key"
          >
            <!-- Client row -->
            <tr class="client-row">
              <td colspan="8">
                <template v-if="client.key !== '__unassigned__'">
                  <strong>{{ client.customer?.name }}</strong>
                  <span
                    v-if="client.customer?.phone"
                    class="ms-2 text-medium-emphasis"
                  >{{ client.customer.phone }}</span>
                </template>
                <strong v-else>Sans client</strong>
                <span class="ms-3 text-medium-emphasis">
                  {{ client.measured_count }}/{{ client.parcel_count }} mesurés · {{ fmtVol(client.volume) }} m³
                </span>
              </td>
              <td colspan="4">
                <template v-if="client.group_ids.length">
                  <select
                    class="cell-select etat-select"
                    :style="{ color: workflowState(client.workflow_status).color }"
                    :value="client.workflow_status"
                    @change="e => changeEtat(client, e.target.value)"
                  >
                    <option
                      v-for="s in WORKFLOW_STATES"
                      :key="s.key"
                      :value="s.key"
                    >
                      {{ s.label }}
                    </option>
                  </select>
                </template>
                <span
                  v-else-if="client.key !== '__unassigned__'"
                  class="text-caption text-medium-emphasis"
                >pas encore groupé</span>
              </td>
              <td>
                <input
                  v-if="client.group_ids.length"
                  class="cell-input ffi-input"
                  placeholder="N° facture"
                  :value="client.external_invoice_ref || ''"
                  @change="e => saveFfi(client, e.target.value)"
                >
              </td>
              <td class="num client-total">
                {{ fmtMga(client.total_mga) }}
              </td>
              <td>
                <VChip
                  v-if="client.complete"
                  size="x-small"
                  color="success"
                  variant="tonal"
                >
                  complet
                </VChip>
              </td>
            </tr>

            <!-- Parcel rows -->
            <tr
              v-for="p in client.visibleParcels"
              :key="p.parcel_id"
              :class="[{ unmeasured: p.price_mga === null, saving: savingRows[p.parcel_id] }, p.highlight_color ? `hl-${p.highlight_color}` : '']"
            >
              <td class="hl-col">
                <VMenu location="bottom start">
                  <template #activator="{ props }">
                    <button
                      v-bind="props"
                      type="button"
                      class="hl-dot"
                      :style="{ backgroundColor: highlightHex(p.highlight_color) || 'transparent' }"
                      :title="p.highlight_color ? 'Changer / retirer le marquage' : 'Marquer ce colis comme trouvé'"
                    />
                  </template>
                  <VList density="compact">
                    <VListItem
                      v-for="c in HIGHLIGHT_COLORS"
                      :key="c.key"
                      @click="setHighlight(p, c.key)"
                    >
                      <template #prepend>
                        <span
                          class="hl-dot static"
                          :style="{ backgroundColor: c.hex }"
                        />
                      </template>
                      <VListItemTitle>{{ c.key }}</VListItemTitle>
                    </VListItem>
                    <VListItem @click="setHighlight(p, null)">
                      <template #prepend>
                        <span class="hl-dot static hl-dot-clear" />
                      </template>
                      <VListItemTitle>Aucune</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>
              </td>
              <td class="shelf">
                <input
                  class="cell-input tiny"
                  placeholder="G"
                  :disabled="!canEdit(p, client) || !!cell(p, 'shelf_location')"
                  :value="cell(p, 'shelf_gondole')"
                  @input="e => setCell(p, 'shelf_gondole', e.target.value)"
                  @change="saveRow(p, client)"
                >
                <input
                  class="cell-input tiny"
                  placeholder="L"
                  :disabled="!canEdit(p, client) || !!cell(p, 'shelf_location')"
                  :value="cell(p, 'shelf_ligne')"
                  @input="e => setCell(p, 'shelf_ligne', e.target.value)"
                  @change="saveRow(p, client)"
                >
                <button
                  v-for="loc in SHELF_LOCATIONS"
                  :key="loc"
                  type="button"
                  class="shelf-loc-btn"
                  :class="{ active: cell(p, 'shelf_location') === loc }"
                  :disabled="!canEdit(p, client)"
                  :title="loc"
                  @click="toggleShelfLocation(p, client, loc)"
                >
                  {{ loc.charAt(0) }}
                </button>
              </td>
              <td>{{ p.agent }}</td>
              <td>
                {{ p.bag_code }}
                <VChip
                  v-if="rowQuantityBadge(p, client.visibleParcels)"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                  class="ms-1"
                >
                  ×{{ rowQuantityBadge(p, client.visibleParcels) }}
                </VChip>
              </td>
              <td class="tracking">
                {{ p.barcode }}
              </td>
              <td
                class="product"
                :title="p.product"
              >
                {{ p.product }}
              </td>
              <td class="muted">
                {{ fmtDims(p.pl) }}
              </td>
              <td class="muted">
                {{ fmtVol(p.pl.volume) }}
              </td>
              <td
                v-for="dim in ['length', 'width', 'height']"
                :key="dim"
                class="measure"
              >
                <input
                  class="cell-input dim"
                  inputmode="decimal"
                  :disabled="!canEdit(p, client)"
                  :title="canEdit(p, client) ? '' : 'Colis sans client — l’attribuer (Claim) avant de mesurer'"
                  :value="cell(p, dim)"
                  @input="e => setCell(p, dim, e.target.value)"
                  @change="saveRow(p, client)"
                  @keydown.enter="e => e.target.blur()"
                >
              </td>
              <td>{{ fmtVol(p.volume) }}</td>
              <td>
                <select
                  class="cell-select"
                  :disabled="!canEdit(p, client)"
                  :value="cell(p, 'type')"
                  @change="e => { setCell(p, 'type', e.target.value); saveRow(p, client) }"
                >
                  <option
                    v-for="c in categories"
                    :key="c"
                    :value="c"
                  >
                    {{ c }}
                  </option>
                </select>
              </td>
              <td class="num">
                {{ fmtMga(p.price_mga) }}
              </td>
              <td class="remark">
                {{ p.remark }}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-if="!filteredClients.length && !isLoading"
          class="pa-6 text-center text-medium-emphasis"
        >
          Aucun colis.
        </div>
      </div>
    </VCard>

    <InsertUnfoundParcelDialog
      v-model="showAddParcel"
      :container="cotation?.container"
      @inserted="onParcelAdded"
    />

    <!-- PAYER: FFI invoice n° is optional -->
    <VDialog
      v-model="paidDialog.open"
      max-width="480"
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon
            icon="tabler-cash-banknote"
            color="error"
            class="me-2"
          />
          {{ paidDialog.client?.customer?.name }} → PAYER
        </VCardTitle>
        <VCardText>
          <VTextField
            v-model="paidDialog.ffi"
            label="N° facture (FFI-…) — facultatif"
            variant="outlined"
            density="compact"
            autofocus
            @keyup.enter="confirmPaid"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="paidDialog.open = false"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="error"
            variant="elevated"
            :loading="paidDialog.saving"
            @click="confirmPaid"
          >
            PAYER
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.cotation-scroll {
  overflow: auto;
  max-block-size: 72vh;
}

.cotation-table {
  border-collapse: collapse;
  font-size: 0.8125rem;
  inline-size: 100%;
}

.cotation-table th {
  position: sticky;
  z-index: 2;
  background: rgb(var(--v-theme-surface));
  border-block-end: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-weight: 600;
  inset-block-start: 0;
  padding-block: 6px;
  padding-inline: 6px;
  text-align: start;
  white-space: nowrap;
}

.cotation-table td {
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-block: 2px;
  padding-inline: 6px;
  white-space: nowrap;
}

.cotation-table .client-row td {
  background: rgba(var(--v-theme-primary), 0.08);
  padding-block: 6px;
}

.cotation-table .client-total {
  font-size: 0.9375rem;
  font-weight: 700;
}

.cotation-table .num {
  font-variant-numeric: tabular-nums;
  text-align: end;
}

.cotation-table .muted {
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.cotation-table .tracking {
  font-family: monospace;
  font-size: 0.75rem;
}

.cotation-table .product {
  max-inline-size: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cotation-table .remark {
  max-inline-size: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cotation-table tr.unmeasured td.measure {
  background: rgba(var(--v-theme-warning), 0.08);
}

.cotation-table tr.saving {
  opacity: 0.55;
}

.cell-input,
.cell-select {
  border: 1px solid rgba(var(--v-border-color), 0.35);
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  color: inherit;
  font: inherit;
  padding-block: 2px;
  padding-inline: 4px;
}

.cell-input:focus,
.cell-select:focus {
  border-color: rgb(var(--v-theme-primary));
  outline: none;
}

.cell-input:disabled,
.cell-select:disabled {
  opacity: 0.4;
}

.cell-input.dim {
  inline-size: 52px;
  text-align: end;
}

.cell-input.tiny {
  inline-size: 30px;
  margin-inline-end: 2px;
  text-align: center;
}

.shelf-loc-btn {
  inline-size: 18px;
  block-size: 18px;
  border: 1px solid rgba(var(--v-border-color), 0.35);
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.shelf-loc-btn.active {
  background: rgb(var(--v-theme-primary));
  border-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.shelf-loc-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ffi-input {
  inline-size: 130px;
}

.etat-select {
  font-weight: 700;
}

/* ---- "found in warehouse" color marker ---- */
.hl-col {
  inline-size: 22px;
  padding-inline: 4px !important;
}

.hl-dot {
  display: inline-block;
  block-size: 14px;
  inline-size: 14px;
  border: 1px solid rgba(var(--v-border-color), 0.5);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
}

.hl-dot.static {
  cursor: default;
}

.hl-dot-clear {
  background: repeating-linear-gradient(
    45deg,
    rgba(var(--v-theme-on-surface), 0.15),
    rgba(var(--v-theme-on-surface), 0.15) 2px,
    transparent 2px,
    transparent 4px
  );
}

.cotation-table tr.hl-red td { background: rgba(239, 68, 68, 0.12); }
.cotation-table tr.hl-orange td { background: rgba(249, 115, 22, 0.12); }
.cotation-table tr.hl-yellow td { background: rgba(234, 179, 8, 0.14); }
.cotation-table tr.hl-green td { background: rgba(34, 197, 94, 0.12); }
.cotation-table tr.hl-blue td { background: rgba(59, 130, 246, 0.12); }
.cotation-table tr.hl-purple td { background: rgba(168, 85, 247, 0.12); }
</style>
